import { FC } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import { Icon } from '@lambdacurry/component-library';
import { selectExtraLineItems, selectMealLineItems } from 'lib/home/util/products';
import { useCurrentCartQuery } from 'lib/home/generated/queries';
import { WeekYear } from 'lib/home/util/week-year';
import { formatDate } from 'lib/util/date';
import { Card, CardContent, Grid } from 'lib/components';
import { RegistrationRouteMap, RegistrationStep, useRegistrationStore } from '../../store';
import { useStripeSetupIntent } from '../../hooks';
import {
  Loading,
  CheckoutForm,
  GettingStartedContent,
  GettingStartedLayout,
  PrivateLayout,
  StripeFormWrapper,
  CartPriceBreakdown,
  CartPromoCode,
  CartItemList,
  GettingStartedFooter
} from '../../components';
import { default as OrderSummaryPattern } from '../../assets/order-summary-pattern.svg';

const OrderSummaryPage: FC = () => {
  const { clientSecret } = useStripeSetupIntent();
  const { showCheckoutItems, checkoutInfo, setCheckoutInfo, setShowCheckoutItems } = useRegistrationStore();
  const { data: cartData } = useCurrentCartQuery();

  if (!cartData || !cartData?.me.subscription) return <Loading />;

  const currentCart = cartData?.me.subscription.current_order.medusa_cart;
  const subscriptionFrequency = cartData?.me.subscription.frequency;
  const mealLineItems = selectMealLineItems(currentCart?.items || []);
  const extraLineItems = selectExtraLineItems(currentCart?.items || []);
  const redirectPath = RegistrationRouteMap.success;
  const shippingDate = formatDate(WeekYear.shippingDate(WeekYear.forNow()));

  return (
    <PrivateLayout>
      <GettingStartedLayout step={RegistrationStep.orderSummary}>
        <GettingStartedContent>
          <Grid>
            <div className="col-span-12 md:col-span-7 lg:col-span-6 lg:col-start-2">
              <Card>
                <OrderSummaryPattern className="w-full h-auto" />

                <CardContent className="min-h-[800px]" variant="slim">
                  <h2>Checkout Information</h2>
                  <StripeFormWrapper clientSecret={clientSecret}>
                    <CheckoutForm
                      redirectPath={redirectPath}
                      persistFunction={values => setCheckoutInfo(values)}
                      initialValues={checkoutInfo}
                    />
                  </StripeFormWrapper>
                </CardContent>
              </Card>
            </div>

            <div className="col-span-12 md:col-span-5 lg:col-span-4">
              <Card>
                <StaticImage
                  className="!block w-full"
                  src="../../assets/order-summary-graphic.jpg"
                  alt="green onion partially chopped on light green background"
                  placeholder="dominantColor"
                  quality={100}
                />

                <CardContent variant="condensed">
                  <div className="flex justify-between">
                    <h3 className="h4 !mb-0">Summary</h3>

                    {showCheckoutItems && (
                      <button data-cy="hide-items" onClick={() => setShowCheckoutItems(false)}>
                        Close your items <Icon name="chevronUp" className="!h-[20px] !w-[20px]" />
                      </button>
                    )}

                    {!showCheckoutItems && (
                      <button data-cy="view-items" onClick={() => setShowCheckoutItems(true)}>
                        See your items <Icon name="chevronDown" className="!h-[20px] !w-[20px]" />
                      </button>
                    )}
                  </div>

                  {showCheckoutItems && (
                    <>
                      <div className="mt-6">
                        <CartItemList items={mealLineItems} />
                      </div>

                      {extraLineItems.length > 0 && (
                        <div className="my-4 mb-6">
                          <h4 className="mb-0">Your Extras</h4>
                          <CartItemList items={extraLineItems} />
                        </div>
                      )}
                    </>
                  )}

                  <CartPromoCode cart={currentCart} />

                  <div>
                    <p className="mt-0">
                      <Icon name="truck" className="mr-2" />
                      First box ships on: <strong className="font-medium text-primary">{shippingDate}</strong>
                    </p>
                    <CartPriceBreakdown cart={currentCart} subscriptionFrequency={subscriptionFrequency} />
                  </div>
                </CardContent>
              </Card>
            </div>
          </Grid>
        </GettingStartedContent>

        <GettingStartedFooter icon="globe" text="Global, mouthwatering ingredients" />
      </GettingStartedLayout>
    </PrivateLayout>
  );
};

export default OrderSummaryPage;
